import { useState, useEffect, useCallback } from 'react';
import { Star, Download, TrendingUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminFeedback() {
  const [data, setData] = useState(null);
  const fetchFeedback = useCallback(() => {
    fetch(`${API}/api/admin/feedback`, { credentials: 'include' }).then(r => r.json()).then(setData).catch(() => { /* non-critical */ });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchFeedback(); }, [fetchFeedback]);

  if (!data) return <div className="text-white/50">Loading feedback...</div>;

  const exportCSV = () => {
    if (!data.responses?.length) return;
    const headers = 'Email,Value,Frequency,Would Miss,Recommend,Type,Likes,Frustrations,Missing\n';
    const rows = data.responses.map(r => `${r.email},${r.value_score},${r.usage_frequency},${r.would_miss},${r.would_recommend},${r.user_type},"${r.likes_most}","${r.frustrations}","${r.missing_feature}"`).join('\n');
    const blob = new Blob([headers + rows], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = url; a.download = 'sendpi_feedback.csv'; a.click();
    const url = URL.createObjectURL(blob);
  };

  return (
    <div data-testid="admin-feedback" className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Feedback Analysis</h1>
        <button onClick={exportCSV} className="px-3 py-1.5 rounded-lg text-xs bg-white/5 text-white/40 hover:text-white flex items-center gap-1">
          <Download className="w-3 h-3" /> Export CSV
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5">
          <div className="text-white/40 text-sm">Responses</div>
          <div className="text-2xl font-bold text-white">{data.total_responses} <span className="text-sm text-white/30">of {data.total_users}</span></div>
          <div className="text-white/30 text-xs">{data.response_rate}% response rate</div>
        </div>
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5">
          <div className="text-white/40 text-sm">Avg Value Score</div>
          <div className="text-2xl font-bold text-white">{data.avg_value_score}/10</div>
        </div>
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5">
          <div className="text-white/40 text-sm">NPS</div>
          <div className={`text-2xl font-bold ${data.nps >= 0 ? 'text-green-400' : 'text-red-400'}`}>{data.nps}</div>
        </div>
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-5">
          <div className="text-white/40 text-sm">PMF Signal</div>
          <div className="mt-2">
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-violet-600 to-cyan-500 rounded-full" style={{ width: `${Math.min(data.pmf_signal / 40 * 100, 100)}%` }} />
            </div>
            <div className="text-xs text-white/40 mt-1">{data.pmf_signal}% "would miss a lot" (target: 40%)</div>
          </div>
        </div>
      </div>

      {/* Would Miss breakdown */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Would Miss It</h2>
        <div className="space-y-2">
          {Object.entries(data.would_miss || {}).map(([key, count]) => {
            const pct = data.total_responses > 0 ? Math.round(count / data.total_responses * 100) : 0;
            return (
              <div key={key} className="flex items-center gap-3">
                <div className="w-36 text-sm text-white/60 truncate">{key.replace(/_/g, ' ')}</div>
                <div className="flex-1 h-5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-violet-600/60 to-cyan-500/60 rounded-full" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-white text-sm w-12 text-right">{pct}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Open text feed */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Open Responses</h2>
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {data.responses?.map((r) => (
            <div key={r.user_id || `fb-${r.submitted_at}`} className="border-b border-white/5 pb-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs bg-white/10 text-white/40 px-2 py-0.5 rounded">{r.user_type}</span>
                <span className="text-xs text-white/30">Value: {r.value_score}/10</span>
                <span className="text-xs text-white/30">{r.would_miss?.replace(/_/g, ' ')}</span>
              </div>
              {r.likes_most && <p className="text-sm text-green-400/80 mb-1">Likes: {r.likes_most}</p>}
              {r.frustrations && <p className="text-sm text-red-400/80 mb-1">Frustrations: {r.frustrations}</p>}
              {r.missing_feature && <p className="text-sm text-amber-400/80">Missing: {r.missing_feature}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
